<div class="content-info st-page">

  <!-- ── Sticky document header ───────────────────────────────────────── -->
  <div class="zf-doc-head">
    <div class="zf-doc-left">
      <a class="zf-doc-back" routerLink="/stocktake"><i class="bi bi-arrow-left"></i> Stock takes</a>
      <div class="zf-doc-title-row">
        <h2 class="zf-doc-title">Stock take session</h2>
        @if (session?.stockTakeSessionNumber) {
        <span class="zf-doc-no">{{ session?.stockTakeSessionNumber }}</span>
        }
        @if (session?.status) {
        <span class="zf-status dot"
          [class.primary]="session?.status === 'DRAFT'"
          [class.info]="session?.status === 'IN_PROGRESS' && session?.sessionType !== 'MASTER'"
          [class.warn]="session?.status === 'IN_PROGRESS' && session?.sessionType === 'MASTER'"
          [class.ok]="session?.status === 'COMPLETED'">
          {{ session?.status === 'IN_PROGRESS'
              ? (session?.sessionType === 'MASTER' ? 'TO REVIEW' : 'TO MERGE')
              : session?.status }}
        </span>
        }
        @if (session?.sessionType) {
        <span class="zf-chip"><span class="zf-chip-k">Type</span>{{ session?.sessionType }}</span>
        }
        <!-- Which store/location this count belongs to — a stock take is always
             scoped to one store, and the variance is computed against that
             store's SOH, so it must be visible here. -->
        @if (session?.store) {
        <span class="zf-chip">
          <i class="bi bi-shop"></i>
          {{ session?.store?.name || session?.store?.storeName }}<ng-container
            *ngIf="session?.store?.storeCode"> ({{ session?.store?.storeCode }})</ng-container>
        </span>
        }
      </div>
    </div>
    <div class="zf-doc-actions">
      @if (session?.status === 'COMPLETED' || session?.status === 'MERGED' || session?.sessionType === 'MASTER') {
      <button type="button" class="btn btn-secondary" (click)="downloadVarianceReport()">
        <i class="bi bi-file-earmark-excel"></i> Variance report
      </button>
      }
      @if (session?.status === 'IN_PROGRESS' && session?.sessionType === 'MASTER') {
      <button type="button" class="btn zf-btn-hero" (click)="completeSession(confirmCompleteModal)">
        Complete stock take
      </button>
      }
    </div>
  </div>

  @if (loading) {
  <div class="zf-sheet-blank">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <span class="s">Loading session…</span>
  </div>
  }

  @if (session && !loading) {

  <!-- ── Session scope (header card) ──────────────────────────────────── -->
  <div class="zf-doc-section">
    <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
      <span class="zf-section-title">Session scope</span>
      <div class="zf-sum-chips">
        <span class="zf-sum-chip" [class.muted]="!storeLabel">
          <span class="k">Store</span>{{ storeLabel || 'Not resolved' }}
        </span>
        <span class="zf-sum-chip"><span class="k">Type</span>{{ session.sessionType }}</span>
        @if (session.startedAt) {
        <span class="zf-sum-chip">
          <span class="k">Started</span><span class="mono">{{ session.startedAt | date:'dd MMM yyyy, HH:mm' }}</span>
        </span>
        }
        <span class="zf-sum-chip"><span class="k">By</span>{{ session.startedByName || getUserName(session.startedBy) }}</span>
        <span class="zf-sum-chip primary">{{ entriesTotal }} {{ entriesTotal === 1 ? 'entry' : 'entries' }}</span>
        @if (session.childSessions?.length) {
        <span class="zf-sum-chip"><span class="k">Merged from</span>{{ session.childSessions?.length }} sessions</span>
        }
      </div>
      <span class="zf-section-toggle">
        {{ headerOpen ? 'Collapse header' : 'Show header' }}
        <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
      </span>
    </div>

    @if (headerOpen) {
    <div class="zf-section-body zf-stack">
      <div class="zf-fgrid" style="--cols: minmax(0,1.4fr) repeat(3, minmax(0,1fr))">
        <div class="zf-field">
          <span class="zf-flabel">Location</span>
          <div class="zf-static"><i class="bi bi-shop"></i>{{ storeLabel || 'Not resolved' }}</div>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Session type</span>
          <div class="zf-static"><i class="bi bi-clipboard-check"></i>{{ session.sessionType }}</div>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Started</span>
          <div class="zf-static" [title]="session.startedByName || getUserName(session.startedBy)">
            @if (session.startedAt) { {{ session.startedAt | date:'medium' }} by {{ session.startedByName || getUserName(session.startedBy) }} }
            @else { — }
          </div>
        </div>
        <div class="zf-field">
          <span class="zf-flabel">Completed</span>
          <div class="zf-static">{{ session.completedAt ? (session.completedAt | date:'medium') : '—' }}</div>
        </div>
      </div>

      @if (session.scannedByNames || session.childSessions?.length) {
      <div class="zf-hr"></div>
      <div class="zf-fgrid" style="--cols: minmax(0,1fr) minmax(0,2fr)">
        <div class="zf-field">
          <span class="zf-flabel">Counted by</span>
          <div class="zf-static" [title]="session.scannedByNames || ''">{{ session.scannedByNames || '—' }}</div>
        </div>
        @if (session.childSessions?.length) {
        <div class="zf-field">
          <span class="zf-flabel">Merged sessions <span class="meta">· {{ session.childSessions?.length }}</span></span>
          <div class="zf-sum-chips">
            @for (child of session.childSessions; track child.id) {
            <a class="zf-sum-chip" [routerLink]="['/stocktake/session', child.id]" [title]="child.startedByName || ''">
              <span class="mono">{{ child.stockTakeSessionNumber }}</span>
            </a>
            }
          </div>
        </div>
        }
      </div>
      }
    </div>
    }
  </div>

  <!-- ── Counting sheet ────────────────────────────────────────────────── -->
  <div class="zf-doc-section zf-lines-card">
    <div class="zf-lines-head">
      <div class="zf-tabs">
        <button type="button" class="zf-tab" [class.active]="activeDetailTab === 'entries'"
          (click)="activeDetailTab = 'entries'">
          Entries<span class="zf-tab-count">{{ entriesTotal }}</span>
        </button>
        @if (session.sessionType === 'MASTER') {
        <button type="button" class="zf-tab" [class.active]="activeDetailTab === 'comparison'"
          (click)="activeDetailTab = 'comparison'">
          SOH comparison<span class="zf-tab-count">{{ comparisonData.length }}</span>
        </button>
        }
      </div>

      @if (activeDetailTab === 'comparison') {
      <span class="zf-count">{{ compLineCountLabel }}</span>
      <div class="zf-pills">
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">
          All<span class="n">{{ pagedComparison.length }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'short'" (click)="lineFilter = 'short'">
          Short<span class="n">{{ pageShortCount }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'over'" (click)="lineFilter = 'over'">
          Over<span class="n">{{ pageOverCount }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'match'" (click)="lineFilter = 'match'">
          Matching<span class="n">{{ pageMatchCount }}</span>
        </button>
        @if (lineFilter !== 'all') {
        <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filter</button>
        }
      </div>
      } @else {
      <span class="zf-count">{{ entriesTotal }} {{ entriesTotal === 1 ? 'entry' : 'entries' }}</span>
      @if (entries.length) {
      <span class="zf-units">{{ entriesPageQtyTotal | number:'1.0-3' }} units on this page</span>
      }
      }

      <div class="zf-lines-tools">
        <label class="zf-search" style="width:250px">
          <i class="bi bi-search"></i>
          <input type="text" placeholder="Search SKU / description..." [(ngModel)]="entrySearch"
            (ngModelChange)="onSearchChange()">
          @if (entrySearch) {
          <button type="button" class="st-clear" title="Clear search" (click)="clearSearch()">&times;</button>
          }
        </label>
      </div>
    </div>

    @if (activeDetailTab === 'comparison') {
    <div class="zf-scoped">
      <span class="k">Scoped</span>
      <span>Counted totals compared against system SOH at <strong>{{ storeLabel || 'this store' }}</strong></span>
      <span class="hint">Pills filter this page · KPIs below cover every compared line</span>
    </div>
    }

    <!-- Entries Tab -->
    @if (activeDetailTab === 'entries') {
    <div id="entries">
      <div class="zf-sheet-wrap">
        <table class="zf-sheet st-sheet">
          <thead>
            <tr>
              <th class="st" style="--l:0; width:42px">#</th>
              <th class="st" style="--l:42px; width:110px">SKU</th>
              <th class="st st-end" style="--l:152px">Description</th>
              <th style="width:96px">Shelf</th>
              <th style="width:84px">Rack</th>
              <th style="width:160px">Scanned by</th>
              <th style="width:130px">Device ID</th>
              <th class="num" style="width:110px">Quantity</th>
              <th class="ctr" style="width:150px">Action</th>
            </tr>
          </thead>
          <tbody>
            @if (entriesLoading) {
            <tr>
              <td colspan="9" class="zf-sheet-empty">
                <div><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
              </td>
            </tr>
            }
            @for (entry of entries; track entry; let i = $index) {
            <tr [class.is-editing]="editingEntryId === entry.id">
              <td class="st zf-idx" style="--l:0">{{ (entriesPage - 1) * entriesLimit + i + 1 }}</td>
              <td class="st" style="--l:42px"><span class="st-sku">{{ entry.sku }}</span></td>
              <td class="st st-end zf-name" style="--l:152px">
                <span class="n" [title]="entry.description">{{ entry.description }}</span>
                <span class="s">{{ entry.scannedAt ? (entry.scannedAt | date:'dd MMM yyyy, HH:mm') : '' }}</span>
              </td>
              <td class="ink3">{{ entry.shelf }}</td>
              <td class="ink3">{{ entry.rack || '-' }}</td>
              <td class="st-text">{{ entry.scannedByName || getUserName(entry.scannedBy) }}</td>
              <td class="dim">{{ entry.deviceId || '-' }}</td>
              <td class="num strong">
                <ng-container *ngIf="editingEntryId !== entry.id; else qtyEdit">
                  {{ entry.quantity | number : "1.0-3" }}
                </ng-container>
                <ng-template #qtyEdit>
                  <input type="number" class="zf-cell-in st-qty" min="0" step="1"
                         [(ngModel)]="editQty"
                         (keyup.enter)="saveEditQty(entry)"
                         (keyup.escape)="cancelEditQty()">
                </ng-template>
              </td>
              <td class="ctr">
                <ng-container *ngIf="canEditQty">
                  <ng-container *ngIf="editingEntryId !== entry.id; else editActions">
                    <button type="button" class="zf-del st-edit" title="Edit counted qty" (click)="startEditQty(entry)">
                      <i class="bi bi-pencil"></i>
                    </button>
                  </ng-container>
                  <ng-template #editActions>
                    <button type="button" class="zf-del st-ok" title="Save" [disabled]="savingQty"
                      (click)="saveEditQty(entry)">
                      <i class="bi bi-check-lg"></i>
                    </button>
                    <button type="button" class="zf-del" title="Cancel" [disabled]="savingQty" (click)="cancelEditQty()">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </ng-template>
                </ng-container>
                <!-- Trace button available if we have trace info -->
                @if (session.childSessions?.length) {
                <button type="button" class="zf-link st-trace" (click)="openTrace(entry, traceModal)">Trace</button>
                }
              </td>
            </tr>
            }
            @if (!entriesLoading && entries.length === 0) {
            <tr>
              <td colspan="9" class="zf-sheet-empty">
                <div>
                  <span class="t">{{ entrySearch ? 'No entries match your search.' : 'No entries found in this session.' }}</span>
                </div>
              </td>
            </tr>
            }
          </tbody>
          @if (entries.length) {
          <tfoot>
            <tr>
              <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ entries.length }} on this page</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="num strong">{{ entriesPageQtyTotal | number:'1.0-3' }}</td>
              <td></td>
            </tr>
          </tfoot>
          }
        </table>
      </div>
      <!-- Entries pagination (server-side). The count line is ALWAYS
           visible so it's obvious the list is paginated; the page buttons
           appear only when there is more than one page. -->
      @if (entriesTotal > 0) {
      <div class="st-foot">
        <span class="st-foot-show">
          Showing {{ (entriesPage - 1) * entriesLimit + 1 }}–{{
            entriesPage * entriesLimit < entriesTotal ? entriesPage * entriesLimit : entriesTotal
          }} of {{ entriesTotal }} entries
        </span>
        @if (entriesPageCount > 1) {
        <div class="st-pager">
          <button type="button" class="btn btn-secondary btn-sm" [disabled]="entriesPage <= 1"
                  (click)="setEntriesPage(entriesPage - 1)">
            <i class="bi bi-chevron-left"></i> Prev
          </button>
          <span class="st-pager-no">{{ entriesPage }} / {{ entriesPageCount }}</span>
          <button type="button" class="btn btn-secondary btn-sm" [disabled]="entriesPage >= entriesPageCount"
                  (click)="setEntriesPage(entriesPage + 1)">
            Next <i class="bi bi-chevron-right"></i>
          </button>
        </div>
        }
      </div>
      }
    </div>
    }

    <!-- Comparison Tab -->
    @if (activeDetailTab === 'comparison') {
    <div id="comparison">
      @if (comparisonLoading) {
      <div class="zf-sheet-blank">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span class="s">Loading comparison data...</span>
      </div>
      }

      @if (!comparisonLoading) {
      <div class="zf-sheet-wrap">
        <table class="zf-sheet st-sheet">
          <thead>
            <tr>
              <th class="st" style="--l:0; width:42px">#</th>
              <th class="st" style="--l:42px; width:110px">SKU</th>
              <th class="st st-end" style="--l:152px">Description</th>
              <th class="num" style="width:110px">Counted</th>
              <th class="num" style="width:110px">System SOH</th>
              <th class="num" style="width:110px">Variance</th>
              <th class="ctr" style="width:90px">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of visibleComparison; track row.item; let i = $index) {
            <tr [class.is-short]="row.item.variance < 0" [class.is-var]="row.item.variance !== 0">
              <td class="st zf-idx" style="--l:0">{{ (compPage - 1) * compLimit + row.index + 1 }}</td>
              <td class="st" style="--l:42px"><span class="st-sku">{{ row.item.sku }}</span></td>
              <td class="st st-end zf-name" style="--l:152px">
                <span class="n" [title]="row.item.description">{{ row.item.description }}</span>
                <span class="s" [class.short]="varianceState(row.item) === 'short'" [class.over]="varianceState(row.item) === 'over'">
                  {{ varianceState(row.item) === 'short' ? 'SHORT' : (varianceState(row.item) === 'over' ? 'OVER' : 'MATCHES SOH') }}
                </span>
              </td>
              <td class="num strong">{{ row.item.totalCounted | number : "1.0-3" }}</td>
              <td class="num ink3">{{ row.item.systemSoh | number : "1.0-3" }}</td>
              <td class="num strong" [class.st-neg]="row.item.variance < 0" [class.st-pos]="row.item.variance > 0">
                {{ row.item.variance > 0 ? '+' : '' }}{{ row.item.variance | number : "1.0-3" }}
              </td>
              <td class="ctr">
                <button type="button" class="zf-link st-trace" (click)="openTrace(row.item, traceModal)">Trace</button>
              </td>
            </tr>
            }
            @if (filteredComparison.length === 0) {
            <tr>
              <td colspan="7" class="zf-sheet-empty">
                <div>
                  <span class="t">{{ comparisonData.length === 0 ? 'No comparison data available.' : 'No items match your search.' }}</span>
                </div>
              </td>
            </tr>
            } @else if (!visibleComparison.length) {
            <tr>
              <td colspan="7" class="zf-sheet-empty">
                <div>
                  <span class="t">No lines match this filter</span>
                  <span class="s">Clear the filter to see all {{ pagedComparison.length }} lines on this page.</span>
                </div>
              </td>
            </tr>
            }
          </tbody>
          @if (pagedComparison.length) {
          <tfoot>
            <tr>
              <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ compLineCountLabel }}</td>
              <td class="num">{{ compPageCountedTotal | number:'1.0-3' }}</td>
              <td class="num dim">{{ compPageSohTotal | number:'1.0-3' }}</td>
              <td class="num strong">{{ compPageVarianceTotal > 0 ? '+' : '' }}{{ compPageVarianceTotal | number:'1.0-3' }}</td>
              <td></td>
            </tr>
          </tfoot>
          }
        </table>
      </div>
      }
      <!-- Comparison pagination (client-side over the computed list).
           Count line always visible; page buttons only when needed. -->
      @if (filteredComparison.length > 0) {
      <div class="st-foot">
        <span class="st-foot-show">
          Showing {{ (compPage - 1) * compLimit + 1 }}–{{
            compPage * compLimit < filteredComparison.length ? compPage * compLimit : filteredComparison.length
          }} of {{ filteredComparison.length }} items
        </span>
        @if (compPageCount > 1) {
        <div class="st-pager">
          <button type="button" class="btn btn-secondary btn-sm" [disabled]="compPage <= 1"
                  (click)="setCompPage(compPage - 1)">
            <i class="bi bi-chevron-left"></i> Prev
          </button>
          <span class="st-pager-no">{{ compPage }} / {{ compPageCount }}</span>
          <button type="button" class="btn btn-secondary btn-sm" [disabled]="compPage >= compPageCount"
                  (click)="setCompPage(compPage + 1)">
            Next <i class="bi bi-chevron-right"></i>
          </button>
        </div>
        }
      </div>
      }
    </div>
    }
  </div>

  <!-- ── Variance strip ────────────────────────────────────────────────── -->
  @if (activeDetailTab === 'comparison' && !comparisonLoading) {
  <div class="zf-order-strip">
    <div class="zf-strip-info">
      <div class="zf-strip-line">
        <span class="dot" [class.ok]="comparisonData.length && compShortCount + compOverCount === 0"></span>
        {{ comparisonData.length }} {{ comparisonData.length === 1 ? 'SKU' : 'SKUs' }} compared against system SOH
      </div>
      <div class="zf-strip-sub">
        @if (!comparisonData.length) {
        <span>No comparison data yet.</span>
        } @else if (compShortCount + compOverCount === 0) {
        <span>Every counted SKU matches system stock.</span>
        } @else {
        <span>{{ compShortCount }} short · {{ compOverCount }} over · {{ compMatchCount }} matching</span>
        <span class="sep">·</span>
        <span>Use Trace on a line to see which session counted it</span>
        }
      </div>
    </div>
    <div class="zf-strip-kpis">
      <div class="zf-kpi">
        <span class="k">Short</span>
        <span class="v" [class.st-neg]="compShortCount > 0">{{ compShortCount }}</span>
      </div>
      <div class="zf-kpi">
        <span class="k">Over</span>
        <span class="v">{{ compOverCount }}</span>
      </div>
      <div class="zf-kpi green">
        <span class="k">Matching</span>
        <span class="v">{{ compMatchCount }}</span>
      </div>
      <div class="zf-kpi dark">
        <span class="k">Net variance</span>
        <span class="v">{{ compNetVariance > 0 ? '+' : '' }}{{ compNetVariance | number:'1.0-3' }} units</span>
      </div>
    </div>
  </div>
  } @else if (activeDetailTab === 'entries') {
  <div class="zf-order-strip">
    <div class="zf-strip-info">
      <div class="zf-strip-line">
        <span class="dot" [class.ok]="entriesTotal > 0"></span>
        {{ entriesTotal }} {{ entriesTotal === 1 ? 'entry' : 'entries' }} in this session · {{ entries.length }} on this page
      </div>
      <div class="zf-strip-sub">
        @if (canEditQty) {
        <span>Counted quantities can still be corrected from the pencil on each row.</span>
        } @else {
        <span>This session is {{ session.status | lowercase }} — quantities are read only.</span>
        }
      </div>
    </div>
    <div class="zf-strip-kpis">
      <div class="zf-kpi">
        <span class="k">Entries</span>
        <span class="v">{{ entriesTotal }}</span>
      </div>
      <div class="zf-kpi dark">
        <span class="k">Units · this page</span>
        <span class="v">{{ entriesPageQtyTotal | number:'1.0-3' }}</span>
      </div>
    </div>
  </div>
  }

  }
</div>

<!-- Trace Modal -->
<ng-template #traceModal let-modal>
  <div class="zf-dialog st-ngb">
    <div class="zf-dialog-head">
      <h3>Backtrace</h3>
      <span class="zf-sum-chip primary"><span class="k">SKU</span>{{ selectedSku }}</span>
      <span class="zf-dialog-sub">Which sessions and users counted this SKU</span>
      <button type="button" class="zf-x" aria-label="Close" (click)="modal.dismiss('Cross click')">✕</button>
    </div>
    <div class="zf-dialog-body">
      @if (traceLoading) {
      <div class="zf-sheet-blank st-dialog-blank">
        <div class="spinner-border text-info" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <span class="s">Fetching trace details...</span>
      </div>
      }

      @if (!traceLoading && traceResult.length === 0) {
      <div class="zf-alert warn">
        <i class="bi bi-exclamation-triangle"></i>
        <span>No specific child session entries found for this SKU/Shelf combination.</span>
      </div>
      }

      @if (traceResult.length > 0) {
      <div class="zf-sheet-wrap st-trace-wrap">
        <table class="zf-sheet">
          <thead>
            <tr>
              <th>Session #</th>
              <th>Scanned by</th>
              <th>Device ID</th>
              <th>Shelf</th>
              <th>Rack</th>
              <th>Time</th>
              <th class="num">Qty found</th>
            </tr>
          </thead>
          <tbody>
            @for (t of traceResult; track t) {
            <tr>
              <td><span class="st-sku">{{ t.sessionNo }}</span></td>
              <td class="st-text">{{ t.user }}</td>
              <td class="dim">{{ t.deviceId || '-' }}</td>
              <td class="ink3">{{ t.shelf }}</td>
              <td class="ink3">{{ t.rack || '-' }}</td>
              <td class="ink3">{{ t.scannedAt | date:'shortTime' }}</td>
              <td class="num strong">{{ t.qty | number : "1.0-3" }}</td>
            </tr>
            }
          </tbody>
          <tfoot>
            <tr>
              <td colspan="6"><span class="k">TOTAL</span>{{ traceResult.length }} {{ traceResult.length === 1 ? 'entry' : 'entries' }}</td>
              <td class="num strong">{{ getTraceTotal() }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      }
    </div>
    <div class="zf-dialog-foot">
      <span class="note">Quantities are listed per child session, shelf and rack.</span>
      <button type="button" class="btn btn-secondary" (click)="modal.close('Close click')">Close</button>
    </div>
  </div>
</ng-template>

<!-- Confirm Complete Modal -->
<ng-template #confirmCompleteModal let-modal>
  <div class="zf-dialog st-ngb">
    <div class="zf-dialog-head">
      <h3>Complete stock take?</h3>
      @if (session?.stockTakeSessionNumber) {
      <span class="zf-sum-chip primary">{{ session?.stockTakeSessionNumber }}</span>
      }
      <button type="button" class="zf-x" aria-label="Close" (click)="modal.dismiss('Cross click')">✕</button>
    </div>
    <div class="zf-dialog-body">
      <p class="st-dialog-p">Are you sure you want to complete this <strong>Stock Take</strong>? This action will update the inventory counts and cannot be undone.</p>

      <div class="st-choices">
        <span class="zf-dialog-k">Select stock take type</span>

        <label class="st-choice" [class.on]="selectedStockTakeType === 'PARTIAL'" for="typePartial">
          <input class="form-check-input" type="radio" name="stockTakeType" id="typePartial"
                 value="PARTIAL" [(ngModel)]="selectedStockTakeType">
          <span class="t">
            <strong>PARTIAL</strong> — update stock <u>only for the SKUs counted</u> in this
            stock take. Every other SKU at this store is left untouched.
          </span>
        </label>

        <label class="st-choice" [class.on]="selectedStockTakeType === 'FULL'" for="typeFull">
          <input class="form-check-input" type="radio" name="stockTakeType" id="typeFull"
                 value="FULL" [(ngModel)]="selectedStockTakeType">
          <span class="t">
            <strong>FULL</strong> — update the counted SKUs <u>and reset every other SKU
            with stock at this store to ZERO</u> (treat this as a complete count of the
            whole store).
          </span>
        </label>

        @if (selectedStockTakeType === 'FULL') {
        <div class="zf-alert danger">
          <i class="bi bi-exclamation-triangle"></i>
          <span>
            <strong>Warning:</strong> FULL will write stock of <strong>0</strong> for every
            SKU at this store that was not counted in this session. Only use this when the
            entire store was physically counted.
          </span>
        </div>
        }
      </div>
    </div>
    <div class="zf-dialog-foot">
      <span class="note">Completing posts the counted quantities to inventory.</span>
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss('Cancel')">Stay</button>
      <button type="button" class="btn zf-btn-hero" (click)="modal.close('Complete')" [disabled]="!selectedStockTakeType">Complete</button>
    </div>
  </div>
</ng-template>
